import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import type { MediaItem } from "@gooner/shared";
import { clipKey } from "@gooner/shared";
import { ClipTile } from "../control/components/ClipTile";
import { Button, Select, cx } from "../control/ui/primitives";

/** Selectable clips-per-page counts for every admin clip list. */
const PAGE_SIZES = [12, 24, 48, 96] as const;
const DEFAULT_PAGE_SIZE = 24;

/**
 * A paginated tile grid shared by every admin clip list. Slices `items` into
 * pages with a user-adjustable page size. `resetKey` jumps back to page 1 when it
 * changes (e.g. a new search, or switching collections).
 */
export function ClipGrid({
  items,
  renderAction,
  renderTopRight,
  onClipClick,
  emptyText,
  resetKey,
}: {
  items: MediaItem[];
  renderAction?: (clip: MediaItem) => ReactNode;
  renderTopRight?: (clip: MediaItem) => ReactNode;
  onClipClick?: (clip: MediaItem) => void;
  emptyText?: string;
  /** Change this to force a jump back to the first page. */
  resetKey?: string;
}) {
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(0);

  const total = items.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  // Jump to the first page whenever the caller signals a fresh list.
  useEffect(() => setPage(0), [resetKey]);
  // Never leave the page index past the end (items or page size shrank).
  useEffect(() => {
    if (page > pageCount - 1) setPage(pageCount - 1);
  }, [page, pageCount]);

  if (total === 0) {
    return emptyText ? <p className="py-6 text-center text-sm text-zinc-500">{emptyText}</p> : null;
  }

  const start = page * pageSize;
  const visible = items.slice(start, start + pageSize);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5">
        {visible.map((clip) => (
          <ClipTile
            key={clipKey(clip)}
            clip={clip}
            actions={renderAction?.(clip)}
            topRight={renderTopRight?.(clip)}
            onClick={onClipClick ? () => onClipClick(clip) : undefined}
          />
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 pt-1 text-xs text-zinc-500">
        <div className="flex items-center gap-2">
          <span>Per page</span>
          <div className="w-20">
            <Select
              value={String(pageSize)}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
            >
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <span>
          {start + 1}–{Math.min(start + pageSize, total)} of {total}
        </span>

        <div className="flex items-center gap-1.5">
          <Button
            size="sm"
            variant="subtle"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            Prev
          </Button>
          <span className={cx("px-1 tabular-nums", pageCount === 1 && "opacity-50")}>
            {page + 1} / {pageCount}
          </span>
          <Button
            size="sm"
            variant="subtle"
            disabled={page >= pageCount - 1}
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
